// =============================================
// COMPONENTE: NODE PALETTE
// =============================================
// Arquivo: NodePalette.tsx
// Descrição: Painel lateral com categorias de nós para arrastar/criar
// =============================================

import React, { useState } from 'react';
import {
  Play, MessageSquare, Keyboard, GitBranch, Zap,
  Cloud, Clock, Database, X,
  Check, Package, User, AlertCircle, FileText, Calendar,
  Search, ChevronDown, ChevronRight, Code, Globe, Server,
  RefreshCw, Mail, AlertTriangle, Settings, List
} from 'lucide-react';
import './NodePalette.css';

// =============================================
// INTERFACES
// =============================================

interface NodeDefinition {
  type: string;
  label: string;
  description: string;
  icon: React.ReactNode;
  color: string;
}

interface NodeCategory {
  id: string;
  label: string;
  icon: React.ReactNode;
  nodes: NodeDefinition[];
}

interface NodePaletteProps {
  onAddNode: (type: string) => void;
  readOnly?: boolean;
}

// =============================================
// DEFINIÇÃO DE CATEGORIAS E NÓS
// =============================================

const nodeCategories: NodeCategory[] = [
  {
    id: 'core',
    label: 'Core',
    icon: <Zap size={16} />,
    nodes: [
      {
        type: 'start',
        label: 'Início',
        description: 'Ponto de partida do fluxo',
        icon: <Play size={16} />,
        color: '#10b981'
      },
      {
        type: 'webhook_trigger',
        label: 'Webhook Trigger',
        description: 'Recebe webhooks externos',
        icon: <Zap size={16} />,
        color: '#f59e0b'
      },
      {
        type: 'scheduler',
        label: 'Agendamento',
        description: 'Executa em horários específicos',
        icon: <Calendar size={16} />,
        color: '#06b6d4'
      },
      {
        type: 'end',
        label: 'Fim',
        description: 'Finaliza a execução',
        icon: <X size={16} />,
        color: '#ef4444'
      }
    ]
  },
  {
    id: 'messaging',
    label: 'Mensagens',
    icon: <MessageSquare size={16} />,
    nodes: [
      {
        type: 'message',
        label: 'Mensagem',
        description: 'Envia mensagem WhatsApp',
        icon: <MessageSquare size={16} />,
        color: '#3b82f6'
      },
      {
        type: 'whatsapp_advanced',
        label: 'WhatsApp Avançado',
        description: 'Todas operações WhatsApp',
        icon: <MessageSquare size={16} />,
        color: '#25d366'
      },
      {
        type: 'email',
        label: 'Email',
        description: 'Envia emails com templates',
        icon: <Mail size={16} />,
        color: '#0ea5e9'
      },
      {
        type: 'input',
        label: 'Capturar Entrada',
        description: 'Aguarda resposta do usuário',
        icon: <Keyboard size={16} />,
        color: '#8b5cf6'
      },
      {
        type: 'menu',
        label: 'Menu Interativo',
        description: 'Menu com opções numeradas',
        icon: <List size={16} />,
        color: '#a855f7'
      },
      {
        type: 'notification',
        label: 'Notificação',
        description: 'Envia notificação push',
        icon: <AlertCircle size={16} />,
        color: '#f59e0b'
      }
    ]
  },
  {
    id: 'logic',
    label: 'Lógica',
    icon: <GitBranch size={16} />,
    nodes: [
      {
        type: 'condition',
        label: 'Condição',
        description: 'Decisão if/else',
        icon: <GitBranch size={16} />,
        color: '#f97316'
      },
      {
        type: 'switch',
        label: 'Switch',
        description: 'Múltiplas saídas condicionais',
        icon: <GitBranch size={16} />,
        color: '#f97316'
      },
      {
        type: 'set_variables',
        label: 'Definir Variáveis',
        description: 'Define múltiplas variáveis',
        icon: <Settings size={16} />,
        color: '#8b5cf6'
      },
      {
        type: 'loop',
        label: 'Loop',
        description: 'For each, While, Repeat',
        icon: <RefreshCw size={16} />,
        color: '#8b5cf6'
      },
      {
        type: 'error_handler',
        label: 'Try/Catch',
        description: 'Tratamento de erros',
        icon: <AlertTriangle size={16} />,
        color: '#ef4444'
      },
      {
        type: 'javascript_function',
        label: 'Função JavaScript',
        description: 'Executa código JS customizado',
        icon: <Code size={16} />,
        color: '#f0db4f'
      },
      {
        type: 'action',
        label: 'Ação',
        description: 'Executa operação',
        icon: <Zap size={16} />,
        color: '#a855f7'
      },
      {
        type: 'delay',
        label: 'Delay/Timer',
        description: 'Aguarda tempo ou agenda',
        icon: <Clock size={16} />,
        color: '#eab308'
      }
    ]
  },
  {
    id: 'integrations',
    label: 'Integrações',
    icon: <Cloud size={16} />,
    nodes: [
      {
        type: 'webhook',
        label: 'Webhook/API',
        description: 'Chama API externa',
        icon: <Cloud size={16} />,
        color: '#14b8a6'
      },
      {
        type: 'http_request',
        label: 'HTTP Request',
        description: 'Requisição HTTP avançada',
        icon: <Globe size={16} />,
        color: '#06b6d4'
      },
      {
        type: 'internal_api',
        label: 'API Interna',
        description: 'Chama rotas internas',
        icon: <Server size={16} />,
        color: '#8b5cf6'
      },
      {
        type: 'database',
        label: 'Banco de Dados',
        description: 'Operações no banco',
        icon: <Database size={16} />,
        color: '#6366f1'
      }
    ]
  },
  {
    id: 'domain',
    label: 'Domínio',
    icon: <Check size={16} />,
    nodes: [
      {
        type: 'checklist',
        label: 'Checklist',
        description: 'Gerencia checklists',
        icon: <Check size={16} />,
        color: '#06b6d4'
      },
      {
        type: 'vehicle',
        label: 'Veículo',
        description: 'Operações com veículos',
        icon: <Package size={16} />,
        color: '#0ea5e9'
      },
      {
        type: 'driver',
        label: 'Motorista',
        description: 'Gerencia motoristas',
        icon: <User size={16} />,
        color: '#8b5cf6'
      },
      {
        type: 'abastecimento',
        label: 'Abastecimento',
        description: 'Registra abastecimentos',
        icon: <Database size={16} />,
        color: '#22c55e'
      },
      {
        type: 'maintenance',
        label: 'Manutenção',
        description: 'Manutenções preventivas/corretivas',
        icon: <Settings size={16} />,
        color: '#f97316'
      },
      {
        type: 'report',
        label: 'Relatório',
        description: 'Gera relatórios',
        icon: <FileText size={16} />,
        color: '#ec4899'
      },
      {
        type: 'schedule',
        label: 'Agendamento',
        description: 'Gerencia agendamentos',
        icon: <Calendar size={16} />,
        color: '#f59e0b'
      }
    ]
  }
];

// =============================================
// COMPONENTE PRINCIPAL
// =============================================

const NodePalette: React.FC<NodePaletteProps> = ({ onAddNode, readOnly = false }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(
    new Set(['core', 'messaging', 'logic'])
  );

  // =============================================
  // FILTRAR NÓS PELA BUSCA
  // =============================================

  const filteredCategories = nodeCategories
    .map(category => ({
      ...category,
      nodes: category.nodes.filter(node =>
        node.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
        node.description.toLowerCase().includes(searchTerm.toLowerCase())
      )
    }))
    .filter(category => category.nodes.length > 0);

  // =============================================
  // TOGGLE CATEGORIA
  // =============================================

  const toggleCategory = (categoryId: string) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(categoryId)) {
      newExpanded.delete(categoryId);
    } else {
      newExpanded.add(categoryId);
    }
    setExpandedCategories(newExpanded);
  };

  // =============================================
  // DRAG START HANDLER
  // =============================================

  const onDragStart = (event: React.DragEvent, nodeType: string) => {
    event.dataTransfer.setData('application/reactflow', nodeType);
    event.dataTransfer.effectAllowed = 'move';
  };

  // =============================================
  // RENDERIZAÇÃO
  // =============================================

  return (
    <div className="node-palette">
      <div className="node-palette-header">
        <h3>Nós</h3>
        <div className="node-palette-search">
          <Search size={16} />
          <input
            type="text"
            placeholder="Buscar nós..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      <div className="node-palette-content">
        {filteredCategories.map(category => (
          <div key={category.id} className="node-category">
            <div
              className="node-category-header"
              onClick={() => toggleCategory(category.id)}
            >
              <div className="node-category-title">
                {category.icon}
                <span>{category.label}</span>
                <span className="node-count">({category.nodes.length})</span>
              </div>
              {expandedCategories.has(category.id) ? (
                <ChevronDown size={16} />
              ) : (
                <ChevronRight size={16} />
              )}
            </div>

            {expandedCategories.has(category.id) && (
              <div className="node-category-items">
                {category.nodes.map(node => (
                  <div
                    key={node.type}
                    className={`node-palette-item ${readOnly ? 'disabled' : ''}`}
                    draggable={!readOnly}
                    onDragStart={(e) => onDragStart(e, node.type)}
                    onClick={() => !readOnly && onAddNode(node.type)}
                    style={{ '--node-color': node.color } as React.CSSProperties}
                  >
                    <div className="node-palette-item-icon" style={{ color: node.color }}>
                      {node.icon}
                    </div>
                    <div className="node-palette-item-content">
                      <div className="node-palette-item-label">{node.label}</div>
                      <div className="node-palette-item-description">{node.description}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}

        {filteredCategories.length === 0 && (
          <div className="node-palette-empty">
            <Search size={32} />
            <p>Nenhum nó encontrado</p>
            <span>Tente outra busca</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default NodePalette;
